import { ReactNode, useEffect, useRef, useState } from 'react'

import { Button, Text } from '@/ui'
import { TypographyStyles } from '@/styles'

import { UNIT_SESSION_TTL_MS, clearUnitLocalStorage } from './banking.constants'

interface UnitSessionGuardProps {
  children: ReactNode
}

export const UnitSessionGuard = ({ children }: UnitSessionGuardProps) => {
  const [isExpired, setIsExpired] = useState(false)
  const hasCleared = useRef(false)

  if (!hasCleared.current) {
    // Per Unit's guidance: clear stale JWT material before presenting a Unit experience.
    clearUnitLocalStorage()
    hasCleared.current = true
  }

  useEffect(() => {
    const startedAt = Date.now()
    const remaining = (): number =>
      UNIT_SESSION_TTL_MS - (Date.now() - startedAt)

    const expire = (): void => {
      clearUnitLocalStorage()
      setIsExpired(true)
    }

    const timeoutId = window.setTimeout(expire, remaining())

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible' && remaining() <= 0) {
        window.clearTimeout(timeoutId)
        expire()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      window.clearTimeout(timeoutId)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [])

  if (isExpired) {
    return <SessionExpiredCard />
  }
  return children
}

const SessionExpiredCard = () => (
  <div className="flex flex-col items-center gap-4 p-8 text-center">
    <Text variant={TypographyStyles.LABEL_MD}>
      Your banking session has expired
    </Text>
    <Text>Refresh the page to continue using banking.</Text>
    <Button onClick={() => window.location.reload()}>Refresh</Button>
  </div>
)
